import { useState, useEffect } from 'react';
import { getCourses } from '../api/api';
import { FaFlask, FaMedal, FaCompass, FaGraduationCap, FaUniversity, FaMapMarkedAlt, FaHandshake, FaClipboardList, FaWhatsapp, FaCheckCircle, FaMapMarkerAlt, FaBullseye, FaPhoneAlt } from 'react-icons/fa';

const WA_MSG = encodeURIComponent('Hello, I want to book a counseling session');
const WA_LINK = `https://wa.me/917417652434?text=${WA_MSG}`;

const staticCourses = {
  'Science Stream': {
    icon: <FaFlask size={20} />,
    desc: 'Comprehensive science coaching for school students',
    courses: [
      { title: 'Physics (IX–X)',       tags: ['Board Exam', 'Foundation'] },
      { title: 'Chemistry (IX–X)',     tags: ['Board Exam', 'Foundation'] },
      { title: 'Mathematics (IX–X)',   tags: ['Board Exam', 'Foundation'] },
      { title: 'Physics (XI–XII)',     tags: ['Board Exam', 'Advanced'] },
      { title: 'Chemistry (XI–XII)',   tags: ['Board Exam', 'Advanced'] },
      { title: 'Mathematics (XI–XII)', tags: ['PCM', 'Advanced'] },
      { title: 'Biology (XI–XII)',     tags: ['PCB', 'Advanced'] },
    ],
  },
  'Competitive Exams': {
    icon: <FaMedal size={20} />,
    desc: 'Intensive preparation for top competitive examinations',
    courses: [
      { title: 'NEET',                   tags: ['Medical', 'National'] },
      { title: 'AMU Entrance',           tags: ['University', 'Entrance'] },
      { title: 'JMI Entrance',           tags: ['University', 'Entrance'] },
      { title: 'CUET Foundation',    tags: ['Universities', 'Entrance'] },
    ],
  },
};

const counselingPoints = [
  { icon: <FaCompass size={32} />, title: 'Right Career Path',        desc: 'Help students identify their strengths and choose the most suitable career direction after 10th, 12th, or Graduation.', color: '#3B82F6' },
  { icon: <FaGraduationCap size={32} />, title: 'Course Guidance',           desc: 'Detailed guidance for B.Tech, BCA, BBA, B.Com, Polytechnic, B.Pharma and other professional programs.', color: '#8B5CF6' },
  { icon: <FaUniversity size={32} />, title: 'College Selection Support', desc: 'Expert advice on selecting the right college based on budget, location, ranking, and career goals.', color: '#10B981' },
  { icon: <FaMapMarkedAlt size={32} />, title: 'Future Planning',           desc: 'Long-term career roadmap including higher studies, job prospects, and skill development strategies.', color: '#F59E0B' },
  { icon: <FaHandshake size={32} />, title: 'One-to-One Mentorship',     desc: 'Personalised sessions with experienced mentors who understand your unique situation and aspirations.', color: '#EF4444' },
  { icon: <FaClipboardList size={32} />, title: 'Admission Assistance',      desc: 'Step-by-step support through entrance forms, deadlines, document preparation, and interview readiness.', color: '#06B6D4' },
];

const stages = [
  {
    stage: 'After 10th',
    color: '#EFF6FF',
    border: '#BFDBFE',
    accent: 'var(--blue)',
    options: ['Science (PCM / PCB)', 'Commerce', 'Arts / Humanities', 'Polytechnic / ITI', 'Vocational Courses'],
  },
  {
    stage: 'After 12th',
    color: '#F0FDF4',
    border: '#BBF7D0',
    accent: 'var(--green-dark)',
    options: ['B.Tech / B.E.', 'MBBS / BDS (NEET)', 'BCA / B.Sc IT', 'BBA / B.Com', 'B.Pharma', 'Law / BA LLB'],
  },
  {
    stage: 'After Graduation',
    color: '#FFFBEB',
    border: '#FDE68A',
    accent: '#B45309',
    options: ['M.Tech / MBA / MCA','LL.M','PG Diploma - Counseling Psychology','PG diploma - Yoga'],
  },
];

function CareerCounselingSection() {
  return (
    <div>
      {/* Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1E3A8A 0%, #1d4ed8 100%)',
        borderRadius: 16,
        padding: '2.5rem 2rem',
        marginBottom: '2.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1.5rem',
      }}>
        <div>
          <div style={{
            display: 'inline-block',
            background: 'rgba(245,158,11,0.2)',
            border: '1px solid rgba(245,158,11,0.5)',
            color: '#FCD34D',
            padding: '0.3rem 1rem',
            borderRadius: 50,
            fontSize: '0.8rem',
            fontWeight: 600,
            marginBottom: '0.8rem',
          }}>🆓 100% Free Session</div>
          <h3 style={{ color: '#fff', fontSize: '1.6rem', fontWeight: 800, marginBottom: '0.5rem' }}>
            Career Counseling for Students
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.95rem', maxWidth: 520 }}>
            Guidance for students after 10th, 12th, and Graduation — choose the right path with expert mentors.
          </p>
        </div>
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#25D366',
            color: '#fff',
            padding: '0.9rem 1.8rem',
            borderRadius: 10,
            fontWeight: 700,
            fontSize: '1rem',
            textDecoration: 'none',
            boxShadow: '0 4px 16px rgba(37,211,102,0.4)',
            whiteSpace: 'nowrap',
            transition: 'all 0.25s',
          }}
        >
          <FaWhatsapp size={20} />
          Book Free Counseling
        </a>
      </div>

      {/* What We Offer */}
      <h4 style={{ color: 'var(--dark)', fontWeight: 700, fontSize: '1.1rem', marginBottom: '1.2rem' }}>
        What We Offer
      </h4>
      <div className="cards-grid" style={{ marginBottom: '2.5rem' }}>
        {counselingPoints.map(({ icon, title, desc, color }) => (
          <div className="card" key={title} style={{ borderTop: '3px solid var(--blue-mid)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.8rem', color }}>{icon}</div>
            <h3>{title}</h3>
            <p>{desc}</p>
          </div>
        ))}
      </div>

      {/* Stage-wise Guidance */}
      <h4 style={{ color: 'var(--dark)', fontWeight: 700, fontSize: '1.1rem', marginBottom: '1.2rem' }}>
        Stage-wise Career Options
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.2rem', marginBottom: '2.5rem' }}>
        {stages.map(({ stage, color, border, accent, options }) => (
          <div key={stage} style={{
            background: color,
            border: `1px solid ${border}`,
            borderRadius: 14,
            padding: '1.5rem',
          }}>
            <div style={{ fontWeight: 800, color: accent, fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <FaMapMarkerAlt size={16} /> {stage}
            </div>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
              {options.map(o => (
                <li key={o} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', color: 'var(--dark)' }}>
                  <span style={{ color: accent, fontWeight: 700 }}><FaCheckCircle size={14} /></span> {o}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div style={{
        background: '#F0FDF4',
        border: '1px solid #BBF7D0',
        borderRadius: 14,
        padding: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1.2rem',
      }}>
        <div>
          <div style={{ fontWeight: 800, color: 'var(--dark)', fontSize: '1.1rem', marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <FaBullseye size={18} color="var(--green-dark)" /> Not sure which career to choose?
          </div>
          <p style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>
            Talk to our expert counselors today — completely free, no obligation.
          </p>
        </div>
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#25D366',
            color: '#fff',
            padding: '0.8rem 1.6rem',
            borderRadius: 10,
            fontWeight: 700,
            fontSize: '0.95rem',
            textDecoration: 'none',
            boxShadow: '0 4px 14px rgba(37,211,102,0.35)',
            whiteSpace: 'nowrap',
          }}
        >
          <FaPhoneAlt size={16} /> Book Free Counseling
        </a>
      </div>
    </div>
  );
}

export default function Courses() {
  const [active, setActive] = useState('Science Stream');
  const [apiCourses, setApiCourses] = useState([]);

  useEffect(() => {
    getCourses().then(r => setApiCourses(r.data)).catch(() => {});
  }, []);

  const tabs = [
    ...Object.keys(staticCourses).map(k => ({ key: k, icon: staticCourses[k].icon })),
    { key: 'Career Counseling', icon: <FaCompass size={16} /> },
  ];

  return (
    <div className="page">
      <div className="page-title">
        <h2>Our <span>Courses</span></h2>
        <p>Choose your path to success</p>
        <div className="accent-line" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center', flexWrap: 'wrap', margin: '2rem 0' }}>
        {tabs.map(({ key, icon }) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            style={{
              padding: '0.6rem 1.4rem',
              borderRadius: 8,
              border: `2px solid ${active === key
                ? key === 'Career Counseling' ? '#16A34A' : 'var(--blue-mid)'
                : 'var(--border)'}`,
              background: active === key
                ? key === 'Career Counseling' ? 'var(--green-dark)' : 'var(--blue)'
                : 'var(--white)',
              color: active === key ? 'var(--white)' : 'var(--gray)',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.25s',
              fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
            }}
          >
            {icon} {key}
          </button>
        ))}
      </div>

      {/* Career Counseling full section */}
      {active === 'Career Counseling' ? (
        <CareerCounselingSection />
      ) : (
        <>
          {/* Category Header */}
          <div style={{
            background: 'var(--blue-pale)',
            border: '1px solid #BFDBFE',
            borderRadius: 12,
            padding: '1.5rem',
            marginBottom: '1.5rem',
            borderLeft: '4px solid var(--blue-mid)',
          }}>
            <h3 style={{ color: 'var(--blue)', fontSize: '1.2rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {staticCourses[active].icon} {active}
            </h3>
            <p style={{ color: 'var(--gray)', marginTop: '0.3rem', fontSize: '0.9rem' }}>
              {staticCourses[active].desc}
            </p>
          </div>

          {/* Course Cards */}
          <div className="cards-grid">
            {staticCourses[active].courses.map(({ title, tags }) => (
              <div className="card" key={title}>
                <h3>{title}</h3>
                <div style={{ marginTop: '0.8rem' }}>
                  {tags.map(t => <span className="card-tag" key={t}>{t}</span>)}
                </div>
              </div>
            ))}
          </div>

          {/* API Courses */}
          {apiCourses.length > 0 && (
            <div style={{ marginTop: '3rem' }}>
              <h3 style={{ color: 'var(--blue)', marginBottom: '1rem', fontWeight: 700 }}>Additional Courses</h3>
              <div className="cards-grid">
                {apiCourses.map(c => (
                  <div className="card" key={c.id}>
                    <h3>{c.title}</h3>
                    <p>{c.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
